import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Icon, Button, Flyout, Tag, TagList, Alert } from "@a-ui/react";
import { Link } from "react-router-dom";
import { openModal } from "../../store/slices/modalReducer";
import { getAvailableActions, getActiveAssignments, hasAnyStartedAssignment, hasUserStoppedAssignment } from '../../helpers/task.helper';
import { showCarrousel, setImages } from "../../store/slices/carrouselReducer";
import { formatDateTime } from "../../helpers/dateTime.helper";
import {
  selectTask,
  fetchTaskAttachments,
} from "../../store/slices/tasksReducer";
import Assignments from "../Assignments/Assignments";
import TaskFlyout from "./TaskFlyout";
import Location from "../Location/Location";

import "../../styles/taskListItem.scss";

const TaskFilter = (props) => {
  const [x, setx] = useState();
  const [direction, setDirection] = useState(true);
  const [readyForRelease, setreadyForRelease] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const { user } = useSelector(state => state.user);
  const taskRef = React.createRef();
  const dispatch = useDispatch();
  const actions = getAvailableActions(props.task, user, false, () => {}, dispatch);
  const { pause } = actions;
  const { open, success } = useSelector((state) => state.modal);
  const { selectedTask } = useSelector((state) => state.tasks);

  const started = props.task.active;
  useEffect(() => {
    if (taskRef.current && !open) {
      if (success && selectedTask === props.task.id) {
        setTimeout(() => {
          if (taskRef.current) taskRef.current.style.height = "0px";
          setTimeout(() => setDeleted(true), 280);
        }, 10);
      } else if (!success) {
        setreadyForRelease(false);
        taskRef.current.style.marginLeft = `0px`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, open, selectedTask, props.task.id]);

  const start = (event) => {
    const touch = event.changedTouches[0];
    setx(touch.pageX);
  };

  const move = (event) => {
    const touch = event.changedTouches[0];
    const windowWith = window.innerWidth;
    const taskholder = event.target.closest(".taskholder");
    let delta = touch.pageX - x;
    const absDelta = Math.abs(delta);
    delta > 0 ? setDirection(true) : setDirection(false);

    if (absDelta > windowWith * 0.5) {
      setreadyForRelease(true);
      delta = delta > 0 ? windowWith * 0.8 : windowWith * 0.8 * -1;
    } else {
      setreadyForRelease(false);
    }

    if (absDelta > 2) {
      taskRef.current.style.marginLeft = `${delta}px`;
      taskholder.style.background = delta > 0 ? "#006e33" : "#cf0039";
    } else {
      taskRef.current.style.marginLeft = "0px";
      taskholder.style.background = "#ffffff";
    }
  };

  const end = (event) => {
    setreadyForRelease(false);
    const touch = event.changedTouches[0];
    const taskholder = event.target.closest(".taskholder");
    const windowWith = window.innerWidth;
    let delta = touch.pageX - x;
    if (Math.abs(delta) > windowWith * 0.5) {
      delta = delta > 0 ? windowWith * 1.1 : windowWith * 1.1 * -1;
      taskRef.current.style.marginLeft = `${delta}px`;
      taskRef.current.style.height = `${taskRef.current.offsetHeight}px`;
      dispatch(selectTask(props.task.id));
      if (delta > 0) {
        dispatch(openModal("CompleteTask"));
      } else {
        dispatch(openModal("CancelTask"));
      }
    } else {
      setx(0);
      taskRef.current.style.marginLeft = `0px`;
      setTimeout(() => {
        taskholder.style.background = "#ffffff";
      }, 280);
    }
  };

  /**
   * Get active assignees for task and check if current user is part of the returned list
   */
  const activeAssignments = getActiveAssignments(props.task.taskAssignments);
  const userHasActiveAssignment = activeAssignments.some(assignment => 
    assignment.todoAppUser.maccount.toLowerCase() === user.userName.toLowerCase()
  );
  const userHasStoppedAssignment = hasUserStoppedAssignment(props.task.taskAssignments, user.userName);
  
  // Show warning if:
  // 1. In "mijn taken" tab and user doesn't have active assignment (was reassigned)
  // 2. In "collega's" tab and user has a stopped assignment (task was previously theirs)
  const myTaskIsNotMyTask = (props.filter === "mijntaken" && !userHasActiveAssignment) ||
                            (props.filter === "collegas" && userHasStoppedAssignment && !userHasActiveAssignment);
  if (deleted) return;

  return (
    <div
      className={`taskholder ${started && "started"} ${
        readyForRelease && "centerIcon"
      }`}
    >
      {/* {readyForRelease && 'now'} */}
      {direction && (
        <div className="left">
          <Icon
            name={readyForRelease ? "ai-check-1" : "ai-arrow-button-right-1"}
            className="u-margin-right-xxs"
          />
          Taak voltooien
        </div>
      )}
      {!direction && (
        <div className="right">
          Taak afbreken
          <Icon
            name={readyForRelease ? "ai-close" : "ai-arrow-button-left-1"}
            className="u-margin-left-xxs"
          />
        </div>
      )}
      <div
        ref={taskRef}
        className="task u-margin-bottom-xs"
        onTouchMove={move}
        onTouchStart={start}
        onTouchEnd={end}
      >
        {myTaskIsNotMyTask && (<div className="u-margin-bottom-s">
            <Alert
              inline
              theme="warning"
              titleId="aui-alert-title"
            >
              Deze taak werd achterliggend aan iemand anders toegewezen. Je kan de <Link to={`/task/${props.task.id}`}>taak terug claimen</Link>.
            </Alert>
          </div>)}
        <div className="taskheader">
          <div className="tasklink">
            <Link to={`/task/${props.task.id}`} className="h3">
              {props.task.name}
            </Link>
            { started && !hasAnyStartedAssignment(props.task.taskAssignments) && (
              <span className="a-label a-label--success u-margin-top-xs">
                Gepauzeerd
              </span>
            )}
            { started && hasAnyStartedAssignment(props.task.taskAssignments) && (
              <span className="a-label a-label--success u-margin-top-xs">
                Gestart
              </span>
            )}
          </div>
          {!myTaskIsNotMyTask && <Flyout
            orientation="bottom-right"
            hasPadding={false}
            size="small"
            trigger={
              <Button
                className={`a-button marginright ${ !started && "a-button--outlined"} ${started && "a-button--success"}`}
                iconRight="ai-navigation-menu-vertical"
                aria-label="Open actions"
                addOn={{
                  type: "icon",
                  align: "right",
                  iconProps: {
                    name: "ai-arrow-down-1",
                  },
                }}
              >
                Actie
              </Button>
            }
          >
            <TaskFlyout task={props.task} />
          </Flyout>}
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <p className="u-margin-bottom-xs">
              <Location location={props.task.locationDuringAssignment} />
            </p>
            <p className="u-margin-bottom-xs">
              <Icon name="ai-calendar-3" className="u-margin-right-xxs" />
              Uiterste datum: {formatDateTime(props.task.dueDate)}
            </p>
            { props.task.vehicleReservation?.vehicleDescription && <p className="u-margin-bottom-xs">
              <Icon name="ai-car-retro-2" className="u-margin-right-xxs" />
              {props.task.vehicleReservation?.vehicleDescription.split("|||").pop()}
            </p> }
            <p className="u-margin-bottom-xs">
              <Icon name="ai-copy-paste-1" className="u-margin-right-xxs" />
              {props.task.taskType}
            </p>
            <p
              className="u-margin-bottom-xs"
              onClick={() => {
                dispatch(fetchTaskAttachments(props.task.id)).then((result) => {
                  dispatch(
                    setImages(
                      result.payload.map(({ data }) => ({
                        src: data.downloadUrl,
                      }))
                    )
                  );
                });
                dispatch(showCarrousel());
              }}
            >
              <Icon
                name="image-file-landscape"
                className="u-margin-right-xxs"
              />
              <span className=" pointer link">Foto’s</span>
            </p>
            <div className="u-margin-bottom-xs">
            { props.task.tags.length > 0 &&  <TagList>
              { props.task.tags.map((tag) => <Tag key={tag.id} label={tag.name}/>) }
            </TagList> }
            </div>
          </div>
          <div className="col-xs-12 col-md-6 assignment u-margin-bottom-xs">
            <div className="u-margin-bottom-xs">
              {activeAssignments.length > 0 && (
                <Icon
                  name="ai-single-neutral"
                  className="floatleft u-margin-right-xxs"
                />
              )}
              <Assignments assignments={props.task.taskAssignments} />
            </div>
            <div className="description">
              {props.task.description && (
                <div className="u-margin-right-xs">
                  <Icon
                    name="ai-question-circle"
                    className="u-margin-right-xxs"
                  />
                  <span
                    className="u-margin-bottom-xs u-text-primary pointer link"
                    onClick={() => {
                      dispatch(selectTask(props.task.id));
                      dispatch(openModal("Description"));
                    }}
                  >
                    Lees meer
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskFilter;
